import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://itaobuyspread.beauty';
const pagePath = '/articoli/guida-acquisti-itaobuy';
const pageUrl = `${siteUrl}${pagePath}`;

export default function GuidaAcquistiItaobuy() {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />
      <main className="pt-24 md:pt-28 bg-background-50">
        <article className="max-w-[900px] mx-auto px-4 md:px-6 pb-20">
          <div className="mb-10">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-foreground-500 hover:text-foreground-950 transition-colors cursor-pointer mb-6"
            >
              <i className="ri-arrow-left-line"></i>
              {t('article.back_home')}
            </Link>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="inline-block bg-accent-100/70 text-accent-700 text-[11px] px-3 py-1 rounded-full font-label">
                Guida Acquisti
              </span>
              <span className="text-xs text-foreground-400">Tempo di lettura: 13 minuti</span>
            </div>

            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-6">
              Guida Acquisti Itaobuy Spreadsheet: Come Acquistare in Modo Intelligente (2026)
            </h1>

            <p className="text-lg text-foreground-600 leading-relaxed mb-8">
              Benvenuto nella <strong>guida acquisti</strong> definitiva per Itaobuy Spreadsheet. Che tu sia 
              un principiante assoluto o un acquirente esperto, questa guida ti accompagnerà passo dopo passo 
              attraverso l&apos;intero processo di acquisto: dalla ricerca del prodotto perfetto fino alla 
              conferma dell&apos;ordine, con consigli pratici per risparmiare tempo e denaro.
            </p>
          </div>

          <div className="rounded-2xl overflow-hidden mb-12">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20shopping%20concept%20with%20smartphone%20displaying%20product%20interface%20beside%20a%20cup%20of%20espresso%2C%20warm%20wooden%20table%2C%20soft%20morning%20light%20from%20window%2C%20Italian%20cafe%20vibe%2C%20editorial%20ecommerce%20aesthetic%2C%20warm%20amber%20and%20cream%20tones%2C%20modern%20minimal%20composition&width=1600&height=800&seq=article-hero-acquisti&orientation=landscape"
              alt="Guida Acquisti Itaobuy Spreadsheet"
              className="w-full object-cover object-top"
            />
          </div>

          <div className="prose-custom">
            <section className="mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                Prima di Iniziare: Cosa ti Serve per Acquistare
              </h2>
              <p className="text-base text-foreground-600 leading-relaxed mb-6">
                Per utilizzare al meglio <strong>Itaobuy Spreadsheet</strong> e completare i tuoi acquisti 
                in modo fluido e sicuro, ecco cosa ti serve:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 rounded-full bg-accent-500/15 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-global-line text-accent-600"></i>
                  </div>
                  <h3 className="font-label text-sm font-semibold text-foreground-900 mb-2">Connessione Internet</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    Una connessione stabile per navigare lo spreadsheet e il sito principale senza interruzioni.
                  </p>
                </div>

                <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 rounded-full bg-accent-500/15 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-user-3-line text-accent-600"></i>
                  </div>
                  <h3 className="font-label text-sm font-semibold text-foreground-900 mb-2">Account Attivo</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    Un account registrato sulla piattaforma di acquisto collegata per gestire ordini e spedizioni.
                  </p>
                </div>

                <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 rounded-full bg-accent-500/15 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-bank-card-line text-accent-600"></i>
                  </div>
                  <h3 className="font-label text-sm font-semibold text-foreground-900 mb-2">Metodo di Pagamento</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    Una carta di credito, debito o altro metodo di pagamento accettato per completare l&apos;ordine.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                Il Processo di Acquisto: 6 Passaggi Fondamentali
              </h2>
              <p className="text-base text-foreground-600 leading-relaxed mb-6">
                Ecco il <strong>percorso completo</strong> per un acquisto di successo con Itaobuy Spreadsheet:
              </p>

              <div className="space-y-4">
                {[
                  { step: '01', title: 'Scegli la Categoria', desc: 'Naviga tra le categorie dello spreadsheet per trovare il tipo di prodotto che ti interessa. Dal menu principale puoi accedere a tutte le sezioni: scarpe, abbigliamento, accessori e molto altro.' },
                  { step: '02', title: 'Filtra e Confronta', desc: 'All\'interno di ogni categoria, utilizza le informazioni disponibili per filtrare i prodotti in base alle tue preferenze. Confronta prezzi, materiali e caratteristiche tra più opzioni.' },
                  { step: '03', title: 'Verifica con le QC Foto', desc: 'Prima di decidere, apri e analizza attentamente le immagini QC del prodotto. Controlla ogni dettaglio: colore, cuciture, etichette, materiali. È il passaggio più importante per un acquisto sicuro.' },
                  { step: '04', title: 'Leggi la Descrizione Completa', desc: 'Non limitarti alle foto. Leggi tutta la descrizione: materiali, taglie, peso, istruzioni di lavaggio e note del venditore. Una descrizione dettagliata è segno di serietà.' },
                  { step: '05', title: 'Procedi all\'Acquisto', desc: 'Una volta deciso, utilizza il link fornito per andare alla pagina di acquisto. Segui la procedura guidata, inserisci l\'indirizzo di spedizione e scegli il metodo di pagamento.' },
                  { step: '06', title: 'Monitora la Spedizione', desc: 'Dopo aver completato l\'ordine, conserva il numero di tracking e monitora lo stato della spedizione. In caso di ritardi o problemi, contatta il servizio clienti attraverso i canali ufficiali.' },
                ].map((item) => (
                  <div key={item.step} className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                    <div className="flex items-start gap-4">
                      <span className="w-8 h-8 rounded-full bg-accent-500/20 flex items-center justify-center font-label text-sm font-bold text-accent-700 flex-shrink-0">
                        {item.step}
                      </span>
                      <div>
                        <h3 className="font-label text-sm font-semibold text-foreground-900 mb-1">{item.title}</h3>
                        <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                Consigli per Risparmiare sui Tuoi Acquisti
              </h2>
              <p className="text-base text-foreground-600 leading-relaxed mb-6">
                Acquistare in modo intelligente significa anche <strong>risparmiare</strong>. Ecco i migliori 
                consigli per ottenere il massimo dal tuo budget:
              </p>

              <div className="space-y-3">
                {[
                  { icon: 'ri-time-line', title: 'Approfitta degli aggiornamenti', desc: 'I nuovi prodotti aggiunti allo spreadsheet hanno spesso prezzi promozionali. Controlla regolarmente per cogliere le migliori offerte.' },
                  { icon: 'ri-stack-line', title: 'Acquista in bundle', desc: 'Alcuni venditori offrono sconti per acquisti multipli. Se trovi più prodotti che ti piacciono, verifica se ci sono offerte cumulative.' },
                  { icon: 'ri-calendar-check-line', title: 'Pianifica gli acquisti stagionali', desc: 'Acquista prodotti fuori stagione per ottenere prezzi migliori. I capi invernali costano meno in estate e viceversa.' },
                  { icon: 'ri-check-line', title: 'Confronta sempre', desc: 'Non accontentarti del primo prodotto: usa lo spreadsheet per confrontare almeno 3-4 opzioni simili prima di decidere.' },
                  { icon: 'ri-sparkling-line', title: 'Cerca prodotti appena aggiunti', desc: 'I prodotti nuovi spesso hanno prezzi introduttivi più bassi per attirare i primi acquirenti e ricevere feedback.' },
                ].map((tip) => (
                  <div key={tip.title} className="flex items-start gap-3 bg-background-50 border border-background-200/70 rounded-xl p-4">
                    <i className={`${tip.icon} text-accent-500 mt-0.5 flex-shrink-0`}></i>
                    <div>
                      <p className="text-sm font-semibold text-foreground-800 mb-0.5">{tip.title}</p>
                      <p className="text-xs text-foreground-500 leading-relaxed">{tip.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                Spedizione e Consegna: Cosa Aspettarsi
              </h2>
              <p className="text-base text-foreground-600 leading-relaxed mb-4">
                La <strong>spedizione</strong> è una fase cruciale del processo di acquisto. Ecco cosa devi 
                sapere quando acquisti prodotti tramite Itaobuy Spreadsheet:
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse bg-background-50 rounded-xl overflow-hidden border border-background-200/70">
                  <thead>
                    <tr className="bg-accent-100/40">
                      <th className="text-left p-4 font-label text-sm text-foreground-800">Aspetto</th>
                      <th className="text-left p-4 font-label text-sm text-foreground-800">Dettagli</th>
                      <th className="text-left p-4 font-label text-sm text-foreground-800">Consiglio</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-background-200/40">
                      <td className="p-4 text-sm text-foreground-700 font-semibold">Tempi di Consegna</td>
                      <td className="p-4 text-sm text-foreground-600">Variabili in base alla destinazione e al venditore</td>
                      <td className="p-4 text-sm text-foreground-600">Controlla sempre i tempi stimati prima di ordinare</td>
                    </tr>
                    <tr className="border-t border-background-200/40">
                      <td className="p-4 text-sm text-foreground-700 font-semibold">Costi di Spedizione</td>
                      <td className="p-4 text-sm text-foreground-600">Dipende dal peso, dimensioni e destinazione</td>
                      <td className="p-4 text-sm text-foreground-600">Cerca venditori con spedizione gratuita o combinata</td>
                    </tr>
                    <tr className="border-t border-background-200/40">
                      <td className="p-4 text-sm text-foreground-700 font-semibold">Tracciamento</td>
                      <td className="p-4 text-sm text-foreground-600">Numero di tracking fornito dopo la spedizione</td>
                      <td className="p-4 text-sm text-foreground-600">Conserva il tracking number e monitora regolarmente</td>
                    </tr>
                    <tr className="border-t border-background-200/40">
                      <td className="p-4 text-sm text-foreground-700 font-semibold">Imballaggio</td>
                      <td className="p-4 text-sm text-foreground-600">Standard o rinforzato in base al prodotto</td>
                      <td className="p-4 text-sm text-foreground-600">Per prodotti fragili, verifica che l&apos;imballaggio sia adeguato</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                Politiche di Reso e Rimborso
              </h2>
              <p className="text-base text-foreground-600 leading-relaxed mb-4">
                Conoscere le <strong>politiche di reso</strong> prima di acquistare è essenziale. Ecco le 
                informazioni più importanti da tenere a mente:
              </p>
              <ul className="space-y-3 mb-4">
                <li className="flex items-start gap-3 text-sm text-foreground-600">
                  <i className="ri-checkbox-circle-line text-accent-500 mt-0.5"></i>
                  <span><strong>Verifica sempre la politica di reso</strong> del venditore prima di completare l&apos;ordine. Ogni venditore può avere condizioni diverse.</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-foreground-600">
                  <i className="ri-checkbox-circle-line text-accent-500 mt-0.5"></i>
                  <span><strong>Conserva l&apos;imballaggio originale</strong> fino a quando non sei sicuro di voler tenere il prodotto. Per molti resi è richiesto.</span>
                </li>
                <li className="flex items-start gap-3 text-sm text-foreground-600">
                  <i className="ri-checkbox-circle-line text-accent-500 mt-0.5"></i>
                  <span><strong>Documenta eventuali problemi</strong> con foto e video al momento dell&apos;apertura del pacco. Saranno prove utili in caso di contestazione.</span>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                Domande Frequenti sulla Guida Acquisti
              </h2>

              <div className="space-y-4">
                <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                  <h3 className="font-label text-sm font-semibold text-foreground-900 mb-2">
                    Quanto tempo ci vuole per ricevere un prodotto ordinato con Itaobuy Spreadsheet?
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    I tempi di consegna variano in base a diversi fattori: il venditore, la destinazione 
                    e il metodo di spedizione scelto. In generale, le spedizioni verso l&apos;Italia possono 
                    richiedere da 7 a 21 giorni lavorativi. Controlla sempre le informazioni specifiche 
                    nella pagina prodotto.
                  </p>
                </div>

                <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                  <h3 className="font-label text-sm font-semibold text-foreground-900 mb-2">
                    Posso annullare un ordine dopo averlo effettuato?
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    Dipende dal venditore e dallo stato dell&apos;ordine. Se l&apos;ordine non è ancora stato 
                    spedito, potresti essere in grado di annullarlo. Contatta il venditore il prima 
                    possibile attraverso la piattaforma per richiedere l&apos;annullamento.
                  </p>
                </div>

                <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                  <h3 className="font-label text-sm font-semibold text-foreground-900 mb-2">
                    Cosa faccio se il prodotto arriva danneggiato o diverso dalle QC foto?
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    Documenta immediatamente il problema con foto e video. Contatta il venditore attraverso 
                    la piattaforma e apri una contestazione. Le QC foto che hai salvato prima dell&apos;acquisto 
                    saranno la tua prova principale per dimostrare la discrepanza.
                  </p>
                </div>

                <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                  <h3 className="font-label text-sm font-semibold text-foreground-900 mb-2">
                    Ci sono costi doganali per le spedizioni in Italia?
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    Per le spedizioni provenienti da paesi extra-UE, potrebbero applicarsi dazi doganali 
                    e IVA all&apos;importazione. L&apos;importo dipende dal valore del prodotto e dalla categoria 
                    merceologica. Informati sulle normative vigenti prima di effettuare acquisti di valore 
                    elevato.
                  </p>
                </div>

                <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
                  <h3 className="font-label text-sm font-semibold text-foreground-900 mb-2">
                    Come posso contattare il servizio clienti di Itaobuy Spreadsheet?
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    Per assistenza, ti consigliamo di visitare il sito principale collegato a Itaobuy 
                    Spreadsheet, dove troverai tutte le informazioni di contatto e i canali di supporto 
                    ufficiali per ricevere aiuto con i tuoi ordini.
                  </p>
                </div>
              </div>
            </section>

            <section className="bg-accent-100/50 rounded-2xl p-8 md:p-10 text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Pronto a Fare il Tuo Primo Acquisto?
              </h2>
              <p className="text-base text-foreground-600 leading-relaxed mb-8 max-w-lg mx-auto">
                Ora hai tutte le informazioni necessarie per acquistare in modo sicuro e intelligente. 
                                Accedi allo spreadsheet e inizia subito!
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-8 py-3.5 rounded-full font-label text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  itaobuy sheet
                  <i className="ri-arrow-right-line"></i>
                </a>
                <Link
                  to="/articoli/guida-itaobuy-spreadsheet"
                  className="inline-flex items-center justify-center gap-2 border border-foreground-300 text-foreground-800 px-8 py-3.5 rounded-full font-label text-sm hover:bg-foreground-950 hover:text-background-50 hover:border-foreground-950 transition-all cursor-pointer whitespace-nowrap"
                >
                  Guida Completa
                </Link>
              </div>
            </section>

            <div className="border-t border-background-200/70 pt-8">
              <p className="text-sm text-foreground-400 mb-4">
                <i className="ri-links-line mr-1"></i>
                Link interni:
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/" className="text-sm text-accent-700 hover:text-accent-500 underline cursor-pointer">
                  Torna alla Homepage di Itaobuy Spreadsheet
                </Link>
                <span className="text-foreground-300">|</span>
                <Link to="/articoli/prodotti-migliori-itaobuy" className="text-sm text-foreground-500 hover:text-foreground-700 cursor-pointer">
                  I Migliori Prodotti
                </Link>
                <span className="text-foreground-300">|</span>
                <Link to="/articoli/sicurezza-itaobuy-spreadsheet" className="text-sm text-foreground-500 hover:text-foreground-700 cursor-pointer">
                  Guida alla Sicurezza
                </Link>
              </div>
            </div>
          </div>
        </article>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: 'Guida Acquisti Itaobuy Spreadsheet: Come Acquistare in Modo Intelligente (2026)',
              description: "Guida completa agli acquisti su Itaobuy Spreadsheet. Scopri il processo di acquisto passo dopo passo, consigli per risparmiare, informazioni su spedizione, resi e molto altro.",
              author: { '@type': 'Organization', name: 'Itaobuy Spreadsheet Italia' },
              datePublished: '2026-07-14',
              dateModified: '2026-07-14',
              mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl },
              publisher: { '@type': 'Organization', name: 'Itaobuy Spreadsheet Italia', url: siteUrl, logo: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg' },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
                { '@type': 'ListItem', position: 2, name: 'Guida Acquisti Itaobuy Spreadsheet', item: pageUrl },
              ],
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}